import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const QuickActions = ({ onTemplateSelect, onImportData, recentIncidents }) => {
  const templates = [
    {
      id: 'malware',
      name: 'Malware Detection',
      description: 'Pre-filled form for malware incidents',
      icon: 'Bug',
      severity: 'high',
      category: 'malware'
    },
    {
      id: 'phishing',
      name: 'Phishing Attack',
      description: 'Template for phishing incidents',
      icon: 'Mail',
      severity: 'medium',
      category: 'phishing'
    },
    {
      id: 'data_breach',
      name: 'Data Breach',
      description: 'Critical data breach template',
      icon: 'Shield',
      severity: 'critical',
      category: 'data_breach'
    },
    {
      id: 'ddos',
      name: 'DDoS Attack',
      description: 'Distributed denial of service template',
      icon: 'Zap',
      severity: 'high',
      category: 'ddos'
    }
  ];

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'text-error',
      high: 'text-warning',
      medium: 'text-accent',
      low: 'text-success'
    };
    return colors?.[severity] || 'text-muted-foreground';
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 mb-6">
      <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
        <Icon name="Zap" size={20} className="mr-2" />
        Quick Actions
      </h3>
      <div className="space-y-6">
        {/* Templates */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-3">Incident Templates</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {templates?.map((template) => (
              <button
                key={template?.id}
                onClick={() => onTemplateSelect(template)}
                className="p-4 text-left border border-border rounded-lg hover:border-primary hover:bg-muted/50 transition-all duration-200 group"
              >
                <div className="flex items-start justify-between mb-2">
                  <Icon 
                    name={template?.icon} 
                    size={20} 
                    className="text-muted-foreground group-hover:text-primary transition-colors duration-200" 
                  />
                  <span className={`text-xs font-medium uppercase ${getSeverityColor(template?.severity)}`}>
                    {template?.severity}
                  </span>
                </div>
                <h5 className="text-sm font-medium text-foreground mb-1">{template?.name}</h5>
                <p className="text-xs text-muted-foreground">{template?.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Import Options */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-3">Import Data</h4>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onImportData('email')}
              iconName="Mail"
              iconPosition="left"
            >
              From Email
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onImportData('alert')}
              iconName="Bell"
              iconPosition="left"
            >
              From Alert
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onImportData('csv')}
              iconName="FileText"
              iconPosition="left"
            >
              From CSV
            </Button>
          </div>
        </div>

        {/* Recent Incidents */}
        {recentIncidents && recentIncidents?.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-foreground mb-3">Recent Similar Incidents</h4>
            <div className="space-y-2">
              {recentIncidents?.slice(0, 3)?.map((incident) => (
                <div
                  key={incident?.id}
                  className="flex items-center justify-between p-3 bg-muted rounded-md"
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-2 h-2 rounded-full ${getSeverityColor(incident?.severity)}`} />
                    <div>
                      <p className="text-sm font-medium text-foreground">{incident?.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {incident?.category} • {incident?.createdAt}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onTemplateSelect({
                      id: 'similar',
                      data: incident
                    })}
                    iconName="Copy"
                  >
                    Use as Template
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Help & Guidelines */}
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={16} className="text-primary mt-0.5" />
            <div>
              <h5 className="text-sm font-medium text-foreground mb-1">Need Help?</h5>
              <p className="text-xs text-muted-foreground mb-2">
                Follow our incident reporting guidelines for complete and accurate submissions.
              </p>
              <div className="flex space-x-2">
                <Button variant="ghost" size="xs" iconName="BookOpen">
                  Guidelines
                </Button>
                <Button variant="ghost" size="xs" iconName="MessageCircle">
                  Get Support
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickActions;